import { useCallback, useMemo, useState } from 'react';

import type { TreatmentRecordRequestChannelSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import {
  type HipaaPackageSchema,
  useGetPackageQuery,
} from '@/services/api/hipaaPackageService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

export type SendFollowupStep =
  | 'idle'
  | 'updating_channel'
  | 'uploading_pdf'
  | 'sending';

export type MissingRequirement = 'none' | 'channel_contact' | 'hipaa_pdf';

export interface SendFollowupFlowState {
  step: SendFollowupStep;
  missingRequirement: MissingRequirement;
  error: string | null;
}

interface UseSendFollowupFlowParams {
  matterId: string;
  providerId: string;
  requestId: string;
}

interface UseSendFollowupFlowReturn {
  state: SendFollowupFlowState;
  packageData: HipaaPackageSchema | undefined;
  channelData: TreatmentRecordRequestChannelSchema | null;
  isLoadingPackage: boolean;
  isLoadingChannel: boolean;
  hasUsableHipaaPdf: boolean;
  setStep: (step: SendFollowupStep) => void;
  setMissingRequirement: (requirement: MissingRequirement) => void;
  setError: (error: string | null) => void;
  resetState: () => void;
  parseApiError: (error: unknown) => MissingRequirement;
  isChannelMissingContact: (
    channel: 'fax' | 'email',
    channelData: TreatmentRecordRequestChannelSchema | null | undefined,
  ) => boolean;
}

const initialState: SendFollowupFlowState = {
  step: 'idle',
  missingRequirement: 'none',
  error: null,
};

/**
 * Hook to orchestrate the multi-step send follow-up flow.
 * Fetches HIPAA package and channel data, handles validation and error parsing.
 * Data auto-updates when mutations invalidate the cache.
 */
export const useSendFollowupFlow = ({
  matterId,
  providerId,
  requestId,
}: UseSendFollowupFlowParams): UseSendFollowupFlowReturn => {
  const [state, setState] = useState<SendFollowupFlowState>(initialState);

  // Fetch HIPAA package data
  const { data: packageData, isLoading: isLoadingPackage } = useGetPackageQuery(
    {
      matterId,
      providerId,
      requestId,
    },
    {
      skip: !matterId || !providerId || !requestId,
    },
  );

  // Fetch channel data - will auto-refresh when mutations invalidate the cache
  const { data: channelsData, isLoading: isLoadingChannel } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsListQuery(
      {
        matterId,
        providerId,
        requestId,
      },
      {
        skip: !matterId || !providerId || !requestId,
      },
    );

  // Get the first (and typically only) channel
  const channelData = useMemo(() => {
    if (!channelsData || channelsData.length === 0) return null;
    return channelsData[0];
  }, [channelsData]);

  // Check if there's a usable PDF
  const hasUsableHipaaPdf = useMemo(() => {
    if (!packageData) return false;
    return Boolean(
      packageData.rendered_pdf_url ||
        packageData.download_url ||
        packageData.manually_uploaded_url,
    );
  }, [packageData]);

  const setStep = useCallback((step: SendFollowupStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  const setMissingRequirement = useCallback(
    (missingRequirement: MissingRequirement) => {
      setState((prev) => ({ ...prev, missingRequirement }));
    },
    [],
  );

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  /**
   * Parse API error response to determine what's missing
   */
  const parseApiError = useCallback((error: unknown): MissingRequirement => {
    if (!error || typeof error !== 'object') return 'none';

    const errorObj = error as {
      data?: { detail?: string; error_code?: string };
      status?: number;
    };

    if (errorObj.status !== 400) return 'none';

    const detail = errorObj.data?.detail?.toLowerCase() || '';
    const errorCode = errorObj.data?.error_code?.toLowerCase() || '';

    // Check for HIPAA PDF related errors
    if (
      detail.includes('hipaa') ||
      detail.includes('pdf') ||
      detail.includes('package') ||
      errorCode.includes('no_package') ||
      errorCode.includes('no_pdf')
    ) {
      return 'hipaa_pdf';
    }

    // Check for channel/contact related errors
    if (
      detail.includes('fax') ||
      detail.includes('email') ||
      detail.includes('channel') ||
      detail.includes('contact') ||
      errorCode.includes('no_channel') ||
      errorCode.includes('no_fax') ||
      errorCode.includes('no_email')
    ) {
      return 'channel_contact';
    }

    return 'none';
  }, []);

  /**
   * Check if the selected channel is missing contact info
   */
  const isChannelMissingContact = useCallback(
    (
      channel: 'fax' | 'email',
      channelDataArg: TreatmentRecordRequestChannelSchema | null | undefined,
    ): boolean => {
      if (!channelDataArg) return true;

      if (channel === 'fax') {
        return !channelDataArg.fax;
      }

      if (channel === 'email') {
        return !channelDataArg.email;
      }

      return false;
    },
    [],
  );

  return {
    state,
    packageData,
    channelData,
    isLoadingPackage,
    isLoadingChannel,
    hasUsableHipaaPdf,
    setStep,
    setMissingRequirement,
    setError,
    resetState,
    parseApiError,
    isChannelMissingContact,
  };
};
